import { useEffect, useState } from 'react'

import { getFaq, getFaqData } from '../../api/faq'
import LoadingSection from '../LoadingSection/LoadingSection'

import FaqExamplesSection from './FaqExample/FaqExample'
import FaqItems from './FaqItem/FaqItem'
import FaqResponsesSection from './FaqResponse/FaqResponse'
import { IFaqExamplesItem, IFaqResponsesItem } from './faqModels'

import styles from './faq.module.scss'

function Faq() {
    const [faqData, setFaqData] = useState<string[]>([])
    const [faqResponsesData, setFaqResponsesData] = useState<
        IFaqResponsesItem[] | null
    >(null)
    const [selectedFaqItem, setSelectedFaqItem] = useState<string>('')
    const [faqExamplesData, setFaqExamplesData] = useState<
        IFaqExamplesItem[] | null
    >(null)
    const [isLoadingFaq, setIsLoadingFaq] = useState<boolean>(true)
    const [isLoadingResponses, setIsLoadingResponses] = useState<boolean>(false)
    const [isLoadingExamples, setIsLoadingExamples] = useState<boolean>(false)

    const handleApiRequest = async (
        apiFunction: () => Promise<any>,
        loadingFunction: (loading: boolean) => void
    ) => {
        try {
            loadingFunction(true)
            const response = await apiFunction()
            return response.data
        } catch (error) {
            console.error('ERROR:', error)
            throw error
        } finally {
            loadingFunction(false)
        }
    }

    const setLoading = async (loadingValue: boolean) => {
        setIsLoadingResponses(loadingValue)
        setIsLoadingExamples(loadingValue)
    }

    const handleFaqItemClick = async (item: string) => {
        try {
            const responseFaqData = await handleApiRequest(
                () => getFaqData(item),
                setLoading
            )
            setSelectedFaqItem(item)
            setFaqResponsesData(responseFaqData.responses)
            setFaqExamplesData(responseFaqData.examples)
        } catch (error) {
            console.error('ERROR:', error)
        }
    }

    const getFaqDataFromApi = async () => {
        try {
            setLoading(true)
            const responseFaqData = await getFaqData(selectedFaqItem)
            setFaqResponsesData(responseFaqData.data.responses)
            setFaqExamplesData(responseFaqData.data.examples)
        } catch (error) {
            console.error('ERROR:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleAddItemClick = (newItem: string) => {
        const toAdd = newItem.trim() !== '' && !faqData.includes(newItem)
        if (toAdd) {
            const updatedFaqData = [newItem, ...faqData]
            setFaqData(updatedFaqData)
        }
    }

    const clearCurrentFaqDataState = () => {
        setSelectedFaqItem('')
        setFaqResponsesData(null)
        setFaqExamplesData(null)
    }

    const fetchFaqData = async () => {
        try {
            setIsLoadingFaq(true)
            const response = await handleApiRequest(getFaq, setLoading)
            clearCurrentFaqDataState()
            setFaqData(response)
        } catch (error) {
            console.error('ERROR:', error)
        } finally {
            setIsLoadingFaq(false)
        }
    }

    useEffect(() => {
        fetchFaqData()
    }, [])

    return (
        <main className={styles.faq}>
            <div className={styles.faq__header}>
                <h1 className={styles.faq__header__title}>FAQ</h1>
            </div>

            <div className={styles.faq__items}>
                {isLoadingFaq ? (
                    <LoadingSection />
                ) : (
                    faqData && (
                        <FaqItems
                            data={faqData}
                            onItemClick={handleFaqItemClick}
                            onAddItemClick={handleAddItemClick}
                            fetchFaqData={fetchFaqData}
                            clearCurrentFaqDataState={clearCurrentFaqDataState}
                        />
                    )
                )}
            </div>
            <div className={styles.faq__responses}>
                {isLoadingResponses ? (
                    <LoadingSection />
                ) : (
                    faqResponsesData && (
                        <FaqResponsesSection
                            data={faqResponsesData}
                            selectedFaqItem={selectedFaqItem}
                            getDataFromApi={getFaqDataFromApi}
                        />
                    )
                )}
            </div>
            <div className={styles.faq__examples}>
                {isLoadingExamples ? (
                    <LoadingSection />
                ) : (
                    faqExamplesData && (
                        <FaqExamplesSection
                            data={faqExamplesData}
                            selectedFaqItem={selectedFaqItem}
                            getDataFromApi={getFaqDataFromApi}
                        />
                    )
                )}
            </div>
        </main>
    )
}

export default Faq
